"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import type { CameraState, CameraConstraints } from "./camera-types";

interface CameraContextValue {
  state: CameraState;
  stream: MediaStream | null;
  startCamera: (constraints?: CameraConstraints) => Promise<void>;
  stopCamera: () => void;
  switchCamera: () => void;
  setError: (error: string | null) => void;
  capturePhoto: () => Promise<string | null>;
}

const CameraContext = createContext<CameraContextValue | undefined>(undefined);

export function CameraProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CameraState>({
    isActive: false,
    isCapturing: false,
    hasPermission: false,
    error: null,
    facingMode: "environment",
  });

  const [stream, setStream] = useState<MediaStream | null>(null);

  const startCamera = useCallback(async (constraints?: CameraConstraints) => {
    try {
      setState((prev) => ({ ...prev, error: null, isCapturing: false }));

      const facingMode = constraints?.facingMode || state.facingMode;
      const videoConstraints: MediaStreamConstraints = {
        video: {
          facingMode,
          width: constraints?.width || { ideal: 1920 },
          height: constraints?.height || { ideal: 1080 },
        },
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(videoConstraints);
      setStream(mediaStream);
      setState((prev) => ({
        ...prev,
        isActive: true,
        hasPermission: true,
        facingMode,
        error: null,
      }));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to access camera";
      setState((prev) => ({
        ...prev,
        isActive: false,
        hasPermission: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, [state.facingMode]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setState((prev) => ({ ...prev, isActive: false }));
  }, [stream]);

  const switchCamera = useCallback(() => {
    const newFacingMode = state.facingMode === "user" ? "environment" : "user";
    setState((prev) => ({ ...prev, facingMode: newFacingMode }));
    
    // Reiniciar câmera com novo facingMode
    if (state.isActive) {
      stopCamera();
      setTimeout(() => {
        startCamera({ facingMode: newFacingMode });
      }, 100);
    }
  }, [state.facingMode, state.isActive, startCamera, stopCamera]);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const capturePhoto = useCallback(async (): Promise<string | null> => {
    if (!stream) return null;
    
    setState((prev) => ({ ...prev, isCapturing: true }));
    
    try {
      const videoTrack = stream.getVideoTracks()[0];
      if (!videoTrack) return null;
      
      // Create a video element to capture from
      const video = document.createElement('video');
      video.srcObject = stream;
      await video.play();
      
      // Create canvas and capture
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      
      ctx.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      
      video.pause();
      video.srcObject = null;
      
      return dataUrl;
    } catch (error) {
      console.error('Error capturing photo:', error);
      return null;
    } finally {
      setState((prev) => ({ ...prev, isCapturing: false }));
    }
  }, [stream]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const value: CameraContextValue = {
    state,
    stream,
    startCamera,
    stopCamera,
    switchCamera,
    setError,
    capturePhoto,
  };

  return (
    <CameraContext.Provider value={value}>
      {children}
    </CameraContext.Provider>
  );
}

export function useCamera() {
  const context = useContext(CameraContext);
  if (context === undefined) {
    throw new Error("useCamera must be used within a CameraProvider");
  }
  return context;
}
